import { useEffect, useState } from "react";
import axios from "axios";
import { FaXmark } from "react-icons/fa6";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { CONTENT_ROUTES } from "../routes/content-routes";
import { RESUME_ACCEPT_ATTR, validateResumeFile } from "../utils/resume-upload";

const EMPTY_FORM = {
  full_name: "",
  email: "",
  phone: "",
  current_position: "",
  years_experience: "",
  current_ctc: "",
  desired_ctc: "",
  notice_period: "",
  consent_other_positions: "",
  agreed_to_privacy: false,
};

export default function ApplicationModal({
  isOpen,
  onClose,
  jobId,
  jobSlug,
  jobTitle,
}) {
  const [formDetails, setFormDetails] = useState(EMPTY_FORM);
  const [resumeFile, setResumeFile] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (!isOpen) {
      setFormDetails(EMPTY_FORM);
      setResumeFile(null);
      setFieldErrors({});
      setFormError("");
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  }, [isOpen]);

  // Close on Escape, and stop the page behind the modal from scrolling.
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const setField = (name, value) => {
    setFormDetails((prevState) => ({ ...prevState, [name]: value }));
    setFieldErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const nextErrors = {};

    if (!formDetails.full_name.trim())
      nextErrors.full_name = "Full name is required.";
    if (!formDetails.email.trim()) {
      nextErrors.email = "Email address is required.";
    } else if (
      !/^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i.test(
        formDetails.email.trim(),
      )
    ) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (!formDetails.phone.trim())
      nextErrors.phone = "Phone number is required.";
    if (!formDetails.current_position.trim())
      nextErrors.current_position = "Current job position and title is required.";
    if (!formDetails.years_experience.trim())
      nextErrors.years_experience = "Years of experience is required.";
    if (!formDetails.desired_ctc.trim())
      nextErrors.desired_ctc = "Desired CTC is required.";
    if (!formDetails.notice_period.trim())
      nextErrors.notice_period = "Notice period is required.";
    if (!formDetails.consent_other_positions.trim())
      nextErrors.consent_other_positions = "Please select YES or NO.";
    if (!formDetails.agreed_to_privacy)
      nextErrors.agreed_to_privacy =
        "You must agree to the Terms of Use and Privacy Policy.";

    if (!resumeFile) {
      nextErrors.resume = "Please upload your CV/resume before submitting.";
    } else {
      const resumeError = validateResumeFile(resumeFile);
      if (resumeError) nextErrors.resume = resumeError;
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setFormError("Please complete all required fields highlighted in red.");
      return;
    }

    setFieldErrors({});

    if (!executeRecaptcha) {
      setFormError("ReCAPTCHA is not ready. Please try again.");
      return;
    }

    try {
      setIsSubmitting(true);
      const recaptchaToken = await executeRecaptcha("job_application");

      const body = new FormData();
      if (jobId) body.append("job_id", String(jobId));
      if (jobSlug) body.append("job_slug", jobSlug);
      body.append("job_title", jobTitle ?? "");
      body.append("full_name", formDetails.full_name.trim());
      body.append("email", formDetails.email.trim());
      body.append("phone", formDetails.phone.trim());
      body.append("current_position", formDetails.current_position.trim());
      body.append("years_experience", formDetails.years_experience.trim());
      body.append("current_ctc", formDetails.current_ctc.trim());
      body.append("desired_ctc", formDetails.desired_ctc.trim());
      body.append("notice_period", formDetails.notice_period.trim());
      body.append(
        "consent_other_positions",
        formDetails.consent_other_positions,
      );
      body.append("agreed_to_privacy", String(formDetails.agreed_to_privacy));
      body.append("resume_file_name", resumeFile.name);
      body.append("resume_mime_type", resumeFile.type);
      body.append("resume_size_bytes", String(resumeFile.size));
      body.append("submitted_at", new Date().toISOString());
      body.append("source_path", window.location.pathname);
      body.append("recaptchaToken", recaptchaToken);
      body.append("resume", resumeFile, resumeFile.name);

      // No explicit Content-Type: the browser must set it so the multipart
      // boundary is included, otherwise PHP cannot parse the body.
      await axios.post(CONTENT_ROUTES.SUBMIT_JOB_APPLICATION(), body);

      setIsSubmitted(true);
    } catch (error) {
      setFormError(
        error?.response?.data?.error ??
          error?.message ??
          "Failed to submit application. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `block w-full border px-3.5 py-2 focus-visible:outline-none ${
      fieldErrors[field]
        ? "border-red-500 bg-red-50"
        : "border-gray-300 focus:border-[#58b947]"
    }`;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Apply for this position"
      >
        <div className="bg-white border-b border-gray-200 px-6 md:px-8 py-5 flex items-start justify-between gap-4 sticky top-0 z-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-medium">
              Apply for this position
            </h2>
            {jobTitle && (
              <p className="text-lg text-[#58b947] font-medium mt-1">
                {jobTitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <span className="sr-only">Close</span>
            <FaXmark className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {isSubmitted ? (
          <div className="p-6 md:p-8 space-y-6">
            <div className="rounded-md p-4 bg-green-100">
              <p className="font-medium text-green-600" role="status">
                Application submitted successfully.
              </p>
            </div>
            <p className="text-gray-700">
              Thank you for applying. Our recruitment team will review your
              application and contact you if your profile matches the role.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="bg-[#58b947] px-5 py-2 text-white inline-block"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
              <div>
                <label htmlFor="full_name" className="text-sm font-medium">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  id="full_name"
                  type="text"
                  className={`mt-1.5 ${inputClass("full_name")}`}
                  value={formDetails.full_name}
                  onChange={(event) =>
                    setField("full_name", event.target.value)
                  }
                />
                {fieldErrors.full_name && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.full_name}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="text-sm font-medium">
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className={`mt-1.5 ${inputClass("email")}`}
                  value={formDetails.email}
                  onChange={(event) => setField("email", event.target.value)}
                />
                {fieldErrors.email && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="text-sm font-medium">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  className={`mt-1.5 ${inputClass("phone")}`}
                  value={formDetails.phone}
                  onChange={(event) => setField("phone", event.target.value)}
                />
                {fieldErrors.phone && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.phone}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="current_position"
                  className="text-sm font-medium"
                >
                  Current job position and title{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id="current_position"
                  type="text"
                  className={`mt-1.5 ${inputClass("current_position")}`}
                  value={formDetails.current_position}
                  onChange={(event) =>
                    setField("current_position", event.target.value)
                  }
                />
                {fieldErrors.current_position && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.current_position}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="years_experience"
                  className="text-sm font-medium"
                >
                  Years of experience <span className="text-red-500">*</span>
                </label>
                <input
                  id="years_experience"
                  type="text"
                  className={`mt-1.5 ${inputClass("years_experience")}`}
                  value={formDetails.years_experience}
                  onChange={(event) =>
                    setField("years_experience", event.target.value)
                  }
                />
                {fieldErrors.years_experience && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.years_experience}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="current_ctc" className="text-sm font-medium">
                  Current CTC
                </label>
                <input
                  id="current_ctc"
                  type="text"
                  inputMode="decimal"
                  placeholder="Enter current CTC"
                  className={`mt-1.5 ${inputClass("current_ctc")}`}
                  value={formDetails.current_ctc}
                  onChange={(event) =>
                    setField("current_ctc", event.target.value)
                  }
                />
              </div>

              <div>
                <label htmlFor="desired_ctc" className="text-sm font-medium">
                  Desired CTC <span className="text-red-500">*</span>
                </label>
                <input
                  id="desired_ctc"
                  type="text"
                  inputMode="decimal"
                  className={`mt-1.5 ${inputClass("desired_ctc")}`}
                  value={formDetails.desired_ctc}
                  onChange={(event) =>
                    setField("desired_ctc", event.target.value)
                  }
                />
                {fieldErrors.desired_ctc && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.desired_ctc}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="notice_period" className="text-sm font-medium">
                  Notice period <span className="text-red-500">*</span>
                </label>
                <input
                  id="notice_period"
                  type="text"
                  className={`mt-1.5 ${inputClass("notice_period")}`}
                  value={formDetails.notice_period}
                  onChange={(event) =>
                    setField("notice_period", event.target.value)
                  }
                />
                {fieldErrors.notice_period && (
                  <p className="text-sm text-red-600 mt-1">
                    {fieldErrors.notice_period}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="resume" className="text-sm font-medium">
                Upload CV/resume <span className="text-red-500">*</span>
              </label>
              <input
                id="resume"
                type="file"
                accept={RESUME_ACCEPT_ATTR}
                className={`mt-1.5 w-full border px-3.5 py-2 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-[#58b947] file:text-white file:cursor-pointer ${
                  fieldErrors.resume ? "border-red-500" : "border-gray-300"
                }`}
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  if (file) {
                    const resumeError = validateResumeFile(file);
                    if (resumeError) {
                      event.target.value = "";
                      setResumeFile(null);
                      setFieldErrors((prevState) => ({
                        ...prevState,
                        resume: resumeError,
                      }));
                      return;
                    }
                  }
                  setResumeFile(file);
                  setFieldErrors((current) => {
                    if (!current.resume) return current;
                    const next = { ...current };
                    delete next.resume;
                    return next;
                  });
                }}
              />
              <p className="text-sm text-gray-600 mt-1">
                Allowed types: .pdf, .doc, .docx — max 2MB
              </p>
              {fieldErrors.resume && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.resume}
                </p>
              )}
            </div>

            <div className="mt-5">
              <span className="text-sm font-medium">
                Consent for us to offer you other positions{" "}
                <span className="text-red-500">*</span>
              </span>
              <div
                className={`flex gap-6 mt-1.5 border px-3.5 py-2 ${
                  fieldErrors.consent_other_positions
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300"
                }`}
              >
                {["YES", "NO"].map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="consent_other_positions"
                      value={option}
                      checked={formDetails.consent_other_positions === option}
                      onChange={(event) =>
                        setField("consent_other_positions", event.target.value)
                      }
                      className="w-4 h-4 accent-[#58b947]"
                    />
                    <span className="text-base text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
              {fieldErrors.consent_other_positions && (
                <p className="text-sm text-red-600 mt-1">
                  {fieldErrors.consent_other_positions}
                </p>
              )}
            </div>

            <div className="flex items-start gap-3 mt-5">
              <input
                id="agreed_to_privacy"
                type="checkbox"
                checked={formDetails.agreed_to_privacy}
                onChange={(event) =>
                  setField("agreed_to_privacy", event.target.checked)
                }
                className="mt-1 w-4 h-4 accent-[#58b947]"
              />
              <label
                htmlFor="agreed_to_privacy"
                className="text-sm text-gray-600 leading-relaxed cursor-pointer"
              >
                I agree to the Terms of Use and Privacy Policy{" "}
                <span className="text-red-500">*</span>
              </label>
            </div>
            {fieldErrors.agreed_to_privacy && (
              <p className="text-sm text-red-600 mt-1">
                {fieldErrors.agreed_to_privacy}
              </p>
            )}

            {formError && <p className="text-red-500 mt-5">{formError}</p>}

            <div className="md:flex gap-x-6 items-start mt-6">
              <p className="text-sm text-gray-600 md:w-8/12">
                This form is protected by reCAPTCHA. Google's{" "}
                <a
                  href="https://policies.google.com/privacy?hl=en-GB"
                  target="_blank"
                  className="underline"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                {` `}and{` `}
                <a
                  href="https://policies.google.com/terms?hl=en-GB"
                  target="_blank"
                  className="underline"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>
                {` `}apply.
              </p>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#58b947] px-5 py-2 text-white inline-block ml-auto md:mt-0 mt-4 md:w-4/12 disabled:opacity-60"
              >
                {isSubmitting ? "Submitting..." : "Submit application"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
